import React, { useMemo, useState } from 'react';
import type { PiV2Claim, PiV2TreatmentPlan } from '../../../patient-insurance/typesV2';
import { piV2ClaimTone, piV2Money, piV2Pct } from '../../../patient-insurance/utilsV2';
import { Pill } from '../../Pill/Pill';
import { Segmented } from '../../Segmented/Segmented';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

const TP_COLS = 'grid grid-cols-[2fr_5rem_5rem_5rem_auto] items-center gap-3';
const CL_COLS = 'grid grid-cols-[6.5rem_7rem_1fr_4.5rem_4.5rem_4.5rem_4.5rem_auto] items-center gap-2.5';

export function PatientInsuranceTreatmentPlanV2({ plan }: { plan: PiV2TreatmentPlan }) {
  const insPct = piV2Pct(plan.insurance, plan.total);
  const patPct = piV2Pct(plan.patient, plan.total);

  return (
    <PatientProfileSectionCard icon="clipboard" title="Treatment plan coverage" accent="var(--ai-3)">
      {plan.rows.length === 0 ? (
        <p className="m-0 text-[13px] text-[var(--ink-3)]">No pending treatment on file.</p>
      ) : (
        <>
          <div className="mb-2.5 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]">
                Total pending
              </div>
              <div className="mt-0.5 font-[var(--font-display)] text-[28px] font-bold leading-none tracking-[-0.03em] text-[var(--ink-0)]">
                {piV2Money(plan.total)}
              </div>
            </div>
            <div className="text-right text-[12.5px]">
              <span className="font-semibold text-[var(--success)]">{piV2Money(plan.insurance)}</span>{' '}
              <span className="text-[var(--ink-3)]">insurance</span> ·{' '}
              <span className="font-semibold text-[var(--gold)]">{piV2Money(plan.patient)}</span>{' '}
              <span className="text-[var(--ink-3)]">patient</span>
            </div>
          </div>
          <div className="mb-3.5 flex h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
            <div className="h-full bg-[var(--success)]" style={{ width: `${String(insPct)}%` }} />
            <div className="h-full bg-[var(--gold)]" style={{ width: `${String(patPct)}%` }} />
          </div>
          <div className={`${TP_COLS} pb-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]`}>
            <span>Procedure</span>
            <span className="text-right">Total</span>
            <span className="text-right">Insurance</span>
            <span className="text-right">Patient</span>
            <span />
          </div>
          {plan.rows.map((r) => (
            <div
              key={r.procedure}
              className={`${TP_COLS} border-t border-[var(--cream-2)] py-2.5 text-[12.5px]`}
            >
              <span className="min-w-0 truncate font-semibold text-[var(--ink-0)]">{r.procedure}</span>
              <span className="text-right font-[var(--font-mono)] text-[var(--ink-1)]">{piV2Money(r.total)}</span>
              <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--success)]">
                {piV2Money(r.insurance)}
              </span>
              <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--gold)]">
                {piV2Money(r.patient)}
              </span>
              <span className="justify-self-end">
                <Pill tone={r.scheduled ? 'success' : 'neutral'} dot={r.scheduled}>
                  {r.status}
                </Pill>
              </span>
            </div>
          ))}
        </>
      )}
    </PatientProfileSectionCard>
  );
}

type ClaimFilter = 'All' | 'Paid' | 'Pending' | 'Denied';

export function PatientInsuranceClaimsV2({ claims }: { claims: PiV2Claim[] }) {
  const [filter, setFilter] = useState<ClaimFilter>('All');

  const rows = useMemo(
    () =>
      claims.filter((c) => {
        if (filter === 'All') return true;
        if (filter === 'Paid') return c.status === 'paid';
        if (filter === 'Pending') return c.status === 'pending';
        return c.status === 'denied';
      }),
    [claims, filter],
  );

  return (
    <PatientProfileSectionCard
      icon="list"
      title="Claims history"
      accent="var(--gold)"
      subtitle={claims.length === 1 ? '1 claim' : `${String(claims.length)} claims`}
      right={
        <Segmented
          options={['All', 'Paid', 'Pending', 'Denied']}
          value={filter}
          onChange={(v) => {
            setFilter(v as ClaimFilter);
          }}
        />
      }
    >
      <div className={`${CL_COLS} pb-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]`}>
        <span>Date</span>
        <span>Claim #</span>
        <span>Procedure</span>
        <span className="text-right">Billed</span>
        <span className="text-right">Allowed</span>
        <span className="text-right">Insurance</span>
        <span className="text-right">Patient</span>
        <span className="text-right">Status</span>
      </div>
      {rows.length === 0 ? (
        <p className="py-4 text-[13px] text-[var(--ink-3)]">No claims match this filter.</p>
      ) : (
        rows.map((c) => (
          <div key={c.id} className={`${CL_COLS} border-t border-[var(--cream-2)] py-[11px] text-[12px]`}>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-2)]">{c.date}</span>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">{c.id}</span>
            <span className="min-w-0 truncate font-medium text-[var(--ink-0)]">{c.procedure}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{piV2Money(c.billed)}</span>
            {/* Allowed — in the v1 type and never rendered. It is the number
                that explains a write-off when a patient asks why the bill
                differs from the billed amount. */}
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-1)]">{piV2Money(c.allowed)}</span>
            <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--success)]">
              {piV2Money(c.insurance)}
            </span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{piV2Money(c.patient)}</span>
            <span className="justify-self-end">
              <Pill tone={piV2ClaimTone(c.status)} dot={c.status === 'paid'}>
                {c.statusLabel}
              </Pill>
            </span>
          </div>
        ))
      )}
    </PatientProfileSectionCard>
  );
}
